import { type FormArray, type FormControl, type FormGroup } from '@angular/forms';

import {
  type ApiAuthorizationType,
  type ApiParameter,
  type BasicAuthorization,
  type BearerTokenAuthorization,
  type ColumnDataType,
  type ColumnInfo,
  HttpMethod,
  type OAuth2Authorization,
  type OAuth2CredentialType,
  type OAuth2FlowType,
} from '@blaze-shared/api-contracts-domain';
import { type ConnectorTemplate } from '@blaze-shared/connectors-domain';

//#region internal

export enum ApiActionStatus {
  PRISTINE,
  TESTING,
  STAGING,
  CONFIRMED,
  ERROR,
}

export enum ApiBuilderDialogPage {
  Builder,
  ResultPathSelect,
  BodyEditor,
}

export enum ParameterType {
  Dynamic,
  FixedValue,
  Pagination,
}

export enum ParameterPaginationMethod {
  PageNumber = 'pageNumber',
  PageSize = 'maxCount',
  RowOffset = 'offset',
}

export type ApiBuilderParameterName = 'Parameter' | 'ParameterOptional' | 'Pagination';

export type ParameterDataType = Lowercase<
  Extract<
    ColumnDataType,
    'String' | 'Number' | 'Boolean' | 'Array' | 'File' | 'DateTime' | 'Date' | 'Time'
  >
>;

export type FormParameterDataType = ParameterDataType | 'auto';

export type OptionalParameterDefaultValue = string | number | boolean | undefined | Array<any>;

export type SerializedOptionalParameterDefaultValue = string | `${number}` | 'true' | 'false';

export const API_BUILDER_PARAMETERS_PATTERN = 'Parameter|ParameterOptional|Pagination';

export interface ApiBuilderBodyParameter {
  name: string;
}

export interface ApiBuilderParameterDetails {
  type: ParameterType;
  paginationMethod?: ParameterPaginationMethod;
  optional?: boolean;
  dataType?: ParameterDataType;
  defaultValue?: OptionalParameterDefaultValue;
}

export type ApiBuilderParameterExtended = ApiParameter & ApiBuilderParameterDetails;

export interface ApiBuilderTestParameterDefinition {
  name: string;
  optional: boolean;
  value: string;
  dataType?: ParameterDataType;
  defaultValue?: OptionalParameterDefaultValue;
}

export interface ApiBuilderCommonConfig {
  baseUrl: string;
  headers: ApiParameter[];
  apiAuthorizationType: ApiAuthorizationType;
  basicAuthorization?: BasicAuthorization;
  bearerTokenAuthorization?: BearerTokenAuthorization;
  oAuth2Authorization?: OAuth2Authorization;
}

export interface ApiBuilderAction {
  id: number;
  data: ApiBuilderActionData;
  status: ApiActionStatus;
  expanded: boolean;
  response: string;
  resultPath: string;
  parameters: Record<string, any> | null;
}

export const DEFAULT_API_BUILDER_ACTION: ApiBuilderAction = {
  id: 0,
  data: {
    name: '',
    httpMethod: HttpMethod.GET,
    url: '',
    headers: [],
    urlParameters: [],
    bodyParameters: [],
    body: null,
    successStatusCode: '200',
    useCookies: true,
    errorResultPath: '',
  },
  status: ApiActionStatus.PRISTINE,
  expanded: true,
  response: '',
  resultPath: '',
  parameters: null,
};

export const REST_API_CONNECTOR_TEMPLATE: ConnectorTemplate = {
  id: 'Rest Api',
  className: '',
  connectorType: 'CustomApi',
  target: 'RestApi',
  displayName: 'Rest API',
  configuration: {
    configurationType: 'Parameters',
    parameters: [],
    form: [],
  },
};

export interface ApiBuilderActionData {
  body: Record<string, any> | Array<any> | null;
  bodyParameter?: ApiBuilderBodyParameter;
  bodyParameters: ApiBuilderParameterExtended[];
  errorResultPath: string;
  headers: ApiBuilderParameterExtended[];
  httpMethod: HttpMethod;
  isTested?: boolean | null;
  name: string;
  noContent?: boolean;
  resultColumns?: ColumnInfo[];
  successStatusCode: string;
  url: string;
  urlParameters: ApiBuilderParameterExtended[];
  useCookies: boolean;
}

export interface BodyParameterEditDialogData {
  parameter: ApiBuilderParameterExtended;
}

export interface AssignParametersTestValuesDialogData {
  parameters: ApiBuilderTestParameterDefinition[];
}
//#endregion

//#region External

//#endregion

//#region Forms
export interface GeneralCardForm {
  baseUrl: FormControl<string>;
  headers: FormArray<FormGroup<ParametersForm>>;
  apiAuthorizationType: FormControl<ApiAuthorizationType>;
  basicAuthorization: FormGroup<ApiBasicAuthorizationForm>;
  bearerTokenAuthorization: FormGroup<ApiBearerTokenAuthorizationForm>;
  oAuth2Authorization: FormGroup<ApiOauth2AuthorizationForm>;
}

export interface ApiBasicAuthorizationForm {
  username: FormControl<string | null>;
  password: FormControl<string | null>;
}

export interface ApiBearerTokenAuthorizationForm {
  token: FormControl<string | null>;
}

export interface ApiOauth2AuthorizationForm {
  accessToken: FormControl<string | null>;
  authorizeUrl: FormControl<string | null>;
  clientId: FormControl<string | null>;
  clientSecret: FormControl<string | null>;
  isAuthorized: FormControl<boolean>;
  oAuth2CredentialType: FormControl<OAuth2CredentialType>;
  oAuth2FlowType: FormControl<OAuth2FlowType>;
  redirectUrl: FormControl<string | null>;
  refreshToken: FormControl<string | null>;
  scope: FormControl<string | null>;
  tokenUrl: FormControl<string | null>;
}

export enum BodyType {
  Dynamic = 'Dynamic',
  Predefined = 'Predefined',
}

export interface ParametersForm {
  name: FormControl<string>;
  value: FormControl<string>;
}

export interface ParametersExtendedForm extends ParametersForm {
  type: FormControl<ParameterType>;
  paginationMethod: FormControl<ParameterPaginationMethod | null>;
  optional: FormControl<boolean | null>;
}

export interface ApiActionForm {
  name: FormControl<string>;
  url: FormControl<string>;
  httpMethod: FormControl<HttpMethod>;
  headers: ParametersExtendedFormArray;
  urlParameters: ParametersExtendedFormArray;
  successStatusCode: FormControl<string>;
  useCookies: FormControl<boolean>;
  errorResultPath: FormControl<string>;
}

export interface BodyParameterEditForm {
  name: FormControl<string>;
  type: FormControl<ParameterType>;
  optional: FormControl<boolean>;
  paginationMethod: FormControl<ParameterPaginationMethod>;
  dataType: FormControl<FormParameterDataType>;
  defaultValue: FormControl<string>;
}

export type ParametersExtendedFormGroup = FormGroup<ParametersExtendedForm>;

export type ParametersExtendedFormArray = FormArray<FormGroup<ParametersExtendedForm>>;
//#endregion
